import { ArrowDown, ArrowDownToLine, ArrowUp, ArrowUpToLine, Copy, Eye, EyeOff, Lock, MousePointer2, Trash2, Unlock } from 'lucide-react';
import type { EditorPort, EditorSnapshot, LayerSnapshot } from '@/ports/editor';

interface LayersPanelProps {
  readonly editor: EditorPort;
  readonly snapshot: EditorSnapshot;
  readonly onSelect: (id: string) => void;
}

export function LayersPanel({ editor, snapshot, onSelect }: LayersPanelProps) {
  const act = (layer: LayerSnapshot, action: () => void) => {
    onSelect(layer.id);
    action();
  };

  return (
    <div className="panel-stack" data-testid="layers-panel">
      <p className="panel-intro">La primera capa se imprime adelante.</p>
      {snapshot.layers.length === 0 ? (
        <div className="empty-panel-state"><strong>No hay elementos todavía</strong><p>Agregá texto, logo, imagen o PDF.</p></div>
      ) : (
        <ol className="layer-list">
          {snapshot.layers.map((layer) => (
            <li key={layer.id} className={layer.selected ? 'is-selected' : ''}>
              <button
                className="layer-main"
                type="button"
                aria-label={`Seleccionar ${layer.label}`}
                aria-pressed={layer.selected}
                onClick={() => onSelect(layer.id)}
              >
                <span className={`layer-icon layer-icon--${layer.kind}`} aria-hidden="true">{layer.kind === 'text' ? 'Aa' : layer.kind === 'pdf' ? 'PDF' : layer.kind === 'background' ? 'BG' : 'IMG'}</span>
                <span><strong>{layer.label}</strong><small>{layer.locked ? 'Bloqueado' : 'Editable'} · {layer.visible ? 'Visible' : 'Oculto'}</small></span>
                <MousePointer2 aria-hidden="true" size={18} />
              </button>
              {layer.selected ? (
                <div className="layer-actions" aria-label={`Acciones de ${layer.label}`}>
                  <button type="button" disabled={!layer.canMoveUp} title="Adelantar una capa" onClick={() => act(layer, () => editor.moveLayer(layer.id, 'up'))}><ArrowUp /><span>Adelantar</span></button>
                  <button type="button" disabled={!layer.canMoveToFront} title="Traer al frente" onClick={() => act(layer, () => editor.moveLayer(layer.id, 'front'))}><ArrowUpToLine /><span>Traer al frente</span></button>
                  <button type="button" disabled={!layer.canMoveDown} title="Atrasar una capa" onClick={() => act(layer, () => editor.moveLayer(layer.id, 'down'))}><ArrowDown /><span>Atrasar</span></button>
                  <button type="button" disabled={!layer.canMoveToBack} title="Enviar al fondo" onClick={() => act(layer, () => editor.moveLayer(layer.id, 'back'))}><ArrowDownToLine /><span>Enviar al fondo</span></button>
                  {layer.canDuplicate ? <button type="button" title="Duplicar" onClick={() => act(layer, () => editor.duplicate(layer.id))}><Copy /><span>Duplicar</span></button> : null}
                  {layer.canToggleVisibility ? <button type="button" title={layer.visible ? 'Ocultar' : 'Mostrar'} onClick={() => act(layer, () => editor.setVisible(layer.id, !layer.visible))}>{layer.visible ? <EyeOff /> : <Eye />}<span>{layer.visible ? 'Ocultar' : 'Mostrar'}</span></button> : null}
                  {layer.canLock ? <button type="button" title={layer.locked ? 'Desbloquear' : 'Bloquear'} onClick={() => act(layer, () => editor.setLocked(layer.id, !layer.locked))}>{layer.locked ? <Unlock /> : <Lock />}<span>{layer.locked ? 'Desbloquear' : 'Bloquear'}</span></button> : null}
                  {layer.canDelete ? <button className="is-danger" type="button" title="Borrar" disabled={layer.locked && layer.id !== 'catalog-background'} onClick={() => act(layer, () => editor.delete(layer.id))}><Trash2 /><span>Borrar</span></button> : null}
                </div>
              ) : null}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
